import React, { useState } from "react";
import { Card, Upload, message } from "antd";
import { PDFDocument } from "pdf-lib";
import * as pdfjsLib from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { useTranslation } from "react-i18next";
import StandardButton from "../components/StandardButton";
import { 
  ArrowUpOutlined, 
  ArrowDownOutlined, 
  DeleteOutlined,
  PlusOutlined,
  MergeCellsOutlined
} from "@ant-design/icons";

const PdfMerger = () => {
  const [files, setFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const { t } = useTranslation();

  const beforeUpload = async (file) => {
    if (file.type !== "application/pdf") {
      message.error(t("only_pdf_supported"));
      return false;
    }

    const maxSize = 50 * 1024 * 1024;
    if (file.size > maxSize) {
      message.error("File size exceeds 50MB limit");
      return false;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      try {
        const typedArray = new Uint8Array(reader.result);
        const pdf = await pdfjsLib.getDocument({ data: typedArray }).promise;
        const page = await pdf.getPage(1);
        const viewport = page.getViewport({ scale: 0.4 });
        const canvas = document.createElement("canvas");
        const context = canvas.getContext("2d");
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        await page.render({ canvasContext: context, viewport }).promise;
        const dataUrl = canvas.toDataURL("image/png");

        setFiles(prev => [...prev, file]);
        setPreviews(prev => [...prev, { 
          name: file.name, 
          file, 
          preview: dataUrl,
          pageCount: pdf.numPages,
          size: (file.size / 1024 / 1024).toFixed(2) + " MB"
        }]);
        
        message.success(`${file.name} ${t('added')}`);
      } catch (err) {
        message.error(`${t('failed_to_process')} ${file.name}: ${err.message}`);
      }
    };
    reader.readAsArrayBuffer(file);

    return false;
  };

  const handleReorder = (index, direction) => {
    const newPreviews = [...previews];
    const newFiles = [...files];
    const swapIndex = index + direction;
    if (swapIndex < 0 || swapIndex >= previews.length) return;
    
    [newPreviews[index], newPreviews[swapIndex]] = [newPreviews[swapIndex], newPreviews[index]];
    [newFiles[index], newFiles[swapIndex]] = [newFiles[swapIndex], newFiles[index]];
    setPreviews(newPreviews);
    setFiles(newFiles);
  };

  const handleDelete = (index) => {
    const newPreviews = previews.filter((_, i) => i !== index);
    const newFiles = files.filter((_, i) => i !== index);
    setPreviews(newPreviews);
    setFiles(newFiles);
  };

  const handleClearAll = () => {
    setPreviews([]);
    setFiles([]);
  };

  const handleMerge = async () => {
    if (files.length < 2) return message.error(t("upload_at_least_2_pdfs"));
    
    message.loading({ content: t("merging_pdfs"), key: "merge", duration: 0 });

    try {
      const mergedPdf = await PDFDocument.create();

      for (const file of files) {
        const arrayBuffer = await file.arrayBuffer();
        const pdf = await PDFDocument.load(arrayBuffer);
        const copiedPages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
        copiedPages.forEach((page) => mergedPdf.addPage(page));
      }

      const pdfBytes = await mergedPdf.save();
      const blob = new Blob([pdfBytes], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `merged_${Date.now()}.pdf`;
      link.click();
      
      message.success({ content: t("pdfs_merged_successfully"), key: "merge" });
    } catch (err) {
      message.error({ content: t("failed_to_merge_pdfs"), key: "merge" });
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-6xl mx-auto px-4 py-8">
        <Card className="rounded-xl shadow-lg border-0 overflow-hidden bg-white">
          {previews.length === 0 && (
            <div className="min-h-[400px] flex flex-col items-center justify-center">
              <div className="text-center p-8">
                <div className="p-4 bg-blue-100 rounded-full inline-block mb-6">
                  <MergeCellsOutlined className="text-4xl text-blue-600" />
                </div>
                
                <Upload
                  beforeUpload={beforeUpload}
                  showUploadList={false}
                  accept=".pdf"
                  multiple
                >
                  <StandardButton
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                  >
                    {t('select_pdf_files')}
                  </StandardButton>
                </Upload>
                
                <div className="mt-4 text-sm text-gray-400">
                  {t('supported_pdf_max_size')}
                </div>
              </div>
            </div>
          )}

          {previews.length > 0 && (
            <div className="mb-6">
              <div className="flex justify-between items-center mb-4">
                <div className="text-gray-700">
                  {t('selected_pdfs')}: <span className="font-semibold">{previews.length}</span>
                </div>
                <div className="flex gap-2">
                  <Upload
                    beforeUpload={beforeUpload}
                    showUploadList={false}
                    accept=".pdf"
                    multiple
                  >
                    <StandardButton 
                      type="secondary" 
                      size="middle"
                      icon={<PlusOutlined />}
                    >
                      {t('add_more')}
                    </StandardButton>
                  </Upload>
                  {previews.length > 0 && (
                    <StandardButton 
                      type="secondary" 
                      danger
                      size="middle"
                      onClick={handleClearAll}
                    >
                      {t('clear_all')}
                    </StandardButton>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                {previews.map((p, i) => (
                  <div 
                    key={i}
                    className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-sm transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <div className="relative flex-shrink-0">
                        <img 
                          src={p.preview} 
                          alt={p.name} 
                          className="w-20 h-24 object-cover rounded border border-gray-200"
                        />
                        <div className="absolute -top-2 -left-2 bg-blue-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold">
                          {i + 1}
                        </div>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="mb-2">
                          <h4 
                            className="font-medium text-gray-800 truncate text-sm" 
                            title={p.name}
                          >
                            {p.name}
                          </h4>
                          <div className="text-xs text-gray-500 flex items-center gap-2">
                            {p.pageCount && (
                              <span>{p.pageCount} {p.pageCount > 1 ? t('pages') : t('page')}</span>
                            )}
                            {p.size && (
                              <span>• {p.size}</span>
                            )}
                          </div>
                        </div>
                        
                        <div className="flex gap-2">
                          <StandardButton
                            variant="ghost"
                            size="small"
                            block
                            onClick={() => handleReorder(i, -1)}
                            disabled={i === 0}
                            icon={<ArrowUpOutlined />}
                          >
                            {t('up')}
                          </StandardButton>
                          <StandardButton
                            variant="ghost"
                            size="small"
                            block
                            onClick={() => handleReorder(i, 1)}
                            disabled={i === previews.length - 1}
                            icon={<ArrowDownOutlined />}
                          >
                            {t('down')}
                          </StandardButton>
                          <StandardButton
                            variant="ghost"
                            size="small"
                            block
                            danger
                            onClick={() => handleDelete(i)}
                            icon={<DeleteOutlined />}
                          >
                            {t('remove')}
                          </StandardButton>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {files.length > 1 && (
                <div className="text-center pt-4 border-t border-gray-100">
                  <StandardButton
                    onClick={handleMerge}
                    type="primary"
                    size="large"
                    icon={<MergeCellsOutlined />}
                  >
                    {t('merge_pdfs')}
                  </StandardButton>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
      
      <Footer />
    </div>
  );
};

export default PdfMerger;